/**
 * PHASE 2: RUNTIME ACCESS HOOK
 * 
 * Central hook for managing tenant runtime access control, module permissions,
 * and channel compatibility validation throughout the application.
 * 
 * Features:
 * - Dynamic module/channel access validation
 * - Real-time permission checking
 * - Plan template enforcement
 * - Feature override management
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';

interface ModuleDefinition {
  id: string;
  module_name: string;
  display_name: string;
  description: string;
  category: string;
  version: string;
  dependencies: string[];
  required_channels: string[];
  default_config: unknown;
  is_active: boolean;
  is_core_module: boolean;
}

interface ChannelCompatibility {
  id: string;
  channel_name: string;
  module_name: string;
  compatibility_level: string;
  required_features: unknown;
  limitations: unknown;
}

interface PlanTemplate {
  id: string;
  template_name: string;
  display_name: string;
  description: string;
  tier_level: string;
  base_modules: string[];
  available_channels: string[];
  default_limits: unknown;
  allowed_overrides: string[];
  is_active: boolean;
}

interface TenantRuntimeConfig {
  enabled_modules: string[];
  enabled_channels: string[];
  plan_template: string;
  custom_limits: unknown;
  feature_overrides: unknown;
  test_environment_enabled: boolean;
}

interface UseRuntimeAccessReturn {
  // Core data
  modules: ModuleDefinition[];
  channels: string[];
  compatibility: ChannelCompatibility[];
  planTemplates: PlanTemplate[];
  currentConfig: TenantRuntimeConfig | null;

  // Access validation
  hasModuleAccess: (moduleName: string) => boolean;
  hasChannelAccess: (channelName: string) => boolean;
  canUseModuleWithChannel: (moduleName: string, channelName: string) => boolean;

  // Configuration
  updateTenantConfig: (tenantId: string, config: Partial<TenantRuntimeConfig>) => Promise<boolean>;

  // Status
  isLoading: boolean;
  error: string | null;
}

// Module-level cache (persists across component lifecycles)
const CACHE_TTL = 30000; // 30 seconds

let runtimeDataCache: {
  modules: ModuleDefinition[];
  compatibility: ChannelCompatibility[];
  planTemplates: PlanTemplate[];
  tenantConfig: TenantRuntimeConfig | null;
  tenantId: string | null;
  timestamp: number;
} | null = null;

let runtimeInflight: { tenantId: string | null; request: ReturnType<typeof fetchRuntimeData> } | null = null;

/**
 * Modules, channel compatibility and plan templates do not depend on the tenant, so they are read once and
 * shared by every consumer, also by the first one, which mounts before a tenant is selected.
 */
let sharedCatalogs: { at: number; request: Promise<{ modules: unknown; compatibility: unknown; plans: unknown }> } | null = null;

function readCatalogs() {
  if (sharedCatalogs && Date.now() - sharedCatalogs.at < CACHE_TTL) return sharedCatalogs.request;
  const request = Promise.all([
    supabase.from('module_definitions').select('*').eq('is_active', true).order('category', { ascending: true }),
    supabase.from('channel_module_compatibility').select('*'),
    supabase.from('plan_templates').select('*').eq('is_active', true).order('tier_level', { ascending: true }),
  ]).then(([modules, compatibility, plans]) => {
    if (modules.error) throw modules.error;
    if (compatibility.error) throw compatibility.error;
    if (plans.error) throw plans.error;
    return { modules: modules.data, compatibility: compatibility.data, plans: plans.data };
  });
  sharedCatalogs = { at: Date.now(), request };
  request.catch(() => { if (sharedCatalogs?.request === request) sharedCatalogs = null; });
  return request;
}

async function fetchRuntimeData(selectedTenant: { id: string } | null) {
  const [catalogs, tenant] = await Promise.all([
    readCatalogs(),
    selectedTenant
      ? supabase
          .from('tenants')
          .select('enabled_modules, enabled_channels, plan_template, custom_limits, feature_overrides, test_environment_enabled')
          .eq('id', selectedTenant.id)
          .single()
      : Promise.resolve(null),
  ]);
  if (tenant && tenant.error) throw tenant.error;

  let tenantConfig: TenantRuntimeConfig | null = null;
  if (tenant && tenant.data) {
    tenantConfig = {
      enabled_modules: tenant.data.enabled_modules || [],
      enabled_channels: tenant.data.enabled_channels || [],
      plan_template: tenant.data.plan_template || 'starter',
      custom_limits: (tenant.data.custom_limits || {}) as TenantRuntimeConfig['custom_limits'],
      feature_overrides: (tenant.data.feature_overrides || {}) as TenantRuntimeConfig['feature_overrides'],
      test_environment_enabled: tenant.data.test_environment_enabled || false,
    };
  }
  return {
    moduleData: (catalogs.modules || []) as ModuleDefinition[],
    compatibilityData: (catalogs.compatibility || []) as ChannelCompatibility[],
    planData: (catalogs.plans || []) as PlanTemplate[],
    tenantConfig,
  };
}

export interface UseRuntimeAccessOptions {
  /**
   * A caller that already holds what it needs (the channel list of its own tenant) passes true: nothing
   * is read, and `isLoading` is false. The data stays empty.
   */
  skip?: boolean;
}

export const useRuntimeAccess = ({ skip = false }: UseRuntimeAccessOptions = {}): UseRuntimeAccessReturn => {
  const { t } = useTranslation('platformAdmin');
  const { selectedTenant } = useTenantSelection();
  const { isPlatformAdmin } = usePlatformAdmin();

  // Initialize from cache if available and valid for this tenant
  const [modules, setModules] = useState<ModuleDefinition[]>(() => {
    if (runtimeDataCache &&
        runtimeDataCache.tenantId === selectedTenant?.id &&
        Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
      return runtimeDataCache.modules;
    }
    return [];
  });

  const [channels] = useState<string[]>(['whatsapp', 'sms', 'email', 'webchat']);

  const [compatibility, setCompatibility] = useState<ChannelCompatibility[]>(() => {
    if (runtimeDataCache &&
        runtimeDataCache.tenantId === selectedTenant?.id &&
        Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
      return runtimeDataCache.compatibility;
    }
    return [];
  });

  const [planTemplates, setPlanTemplates] = useState<PlanTemplate[]>(() => {
    if (runtimeDataCache &&
        runtimeDataCache.tenantId === selectedTenant?.id &&
        Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
      return runtimeDataCache.planTemplates;
    }
    return [];
  });

  const [currentConfig, setCurrentConfig] = useState<TenantRuntimeConfig | null>(() => {
    if (runtimeDataCache &&
        runtimeDataCache.tenantId === selectedTenant?.id &&
        Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
      return runtimeDataCache.tenantConfig;
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState(() => {
    if (skip) return false;
    // Start with loading false if we have valid cache for this tenant
    if (runtimeDataCache &&
        runtimeDataCache.tenantId === selectedTenant?.id &&
        Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
      return false;
    }
    return true;
  });

  const [error, setError] = useState<string | null>(null);
  const fetchFailCountRef = useRef(0);

  // Load all runtime data
  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return undefined;
    }
    // H-28: none of the four `await supabase...` calls below carry a timeout or
    // an AbortSignal, so a single hung request (RLS deadlock, cold function,
    // stalled network) suspended the async function forever — `finally` never
    // ran, `isLoading` stayed true, and every consumer (AppSidebar's Channels
    // skeleton among them) rendered a permanent "loading" that was actually
    // dead. A watchdog turns "never resolves" into a real error the operator
    // can act on (retry), which is the only shape a loading state is allowed
    // to fail in.
    let timedOut = false;
    const watchdog = setTimeout(() => {
      timedOut = true;
      console.error('useRuntimeAccess: Load timed out after 15s');
      setError(t('runtime.loadFailed'));
      setIsLoading(false);
    }, 15000);

    const loadRuntimeData = async () => {
      try {
        // Check cache first - early return if valid
        if (runtimeDataCache &&
            runtimeDataCache.tenantId === selectedTenant?.id &&
            Date.now() - runtimeDataCache.timestamp < CACHE_TTL) {
          console.log('useRuntimeAccess: Using cached data (age:', Math.round((Date.now() - runtimeDataCache.timestamp) / 1000), 'seconds)');
          clearTimeout(watchdog);
          setModules(runtimeDataCache.modules);
          setCompatibility(runtimeDataCache.compatibility);
          setPlanTemplates(runtimeDataCache.planTemplates);
          setCurrentConfig(runtimeDataCache.tenantConfig);
          setIsLoading(false);
          return; // Skip fetch entirely
        }

        setIsLoading(true);
        setError(null);
        console.log('useRuntimeAccess: Loading runtime data, selectedTenant:', selectedTenant?.id);

        // The four reads are independent: one round trip instead of four in a row. Consumers that mount
        // together (the sidebar and the page) share the request that is already on the wire.
        const tenantKey = selectedTenant?.id ?? null;
        if (!runtimeInflight || runtimeInflight.tenantId !== tenantKey) {
          const request = fetchRuntimeData(selectedTenant);
          const entry = { tenantId: tenantKey, request };
          runtimeInflight = entry;
          const clear = () => { if (runtimeInflight === entry) runtimeInflight = null; };
          request.then(clear, clear);
        }
        const { moduleData, compatibilityData, planData, tenantConfig } = await runtimeInflight.request;
        setModules(moduleData);
        setCompatibility(compatibilityData);
        setPlanTemplates(planData);
        setCurrentConfig(tenantConfig);

        // Update module cache after successful fetch
        runtimeDataCache = {
          modules: moduleData,
          compatibility: compatibilityData,
          planTemplates: planData,
          tenantConfig: tenantConfig,
          tenantId: selectedTenant?.id || null,
          timestamp: Date.now()
        };
        console.log('useRuntimeAccess: Cached runtime data for tenant:', selectedTenant?.id);
        fetchFailCountRef.current = 0;

      } catch (err) {
        console.error('useRuntimeAccess: Error loading runtime data:', err);
        // A late rejection arriving after the watchdog already fired would
        // otherwise silently erase the "load failed, try again" state with
        // the same message a beat later — harmless here, but the `timedOut`
        // guard below keeps both branches from racing to set state.
        if (!timedOut) setError(err instanceof Error ? err.message : t('runtime.loadFailed'));
        fetchFailCountRef.current++;
        if (fetchFailCountRef.current >= 3) {
          console.error('useRuntimeAccess: Circuit breaker — stopping retries, clearing stale tenant reference');
          localStorage.removeItem('selectedTenantId');
        }
      } finally {
        clearTimeout(watchdog);
        if (!timedOut) setIsLoading(false);
      }
    };

    loadRuntimeData();
    return () => clearTimeout(watchdog);
  }, [selectedTenant, t, skip]);

  // Access validation functions
  const hasModuleAccess = (moduleName: string): boolean => {
    if (isPlatformAdmin) return true;
    if (!currentConfig) return false;
    return currentConfig.enabled_modules.includes(moduleName);
  };

  const hasChannelAccess = (channelName: string): boolean => {
    if (isPlatformAdmin) return true;
    if (!currentConfig) return false;
    return currentConfig.enabled_channels.includes(channelName);
  };

  const canUseModuleWithChannel = (moduleName: string, channelName: string): boolean => {
    if (isPlatformAdmin) return true;
    
    // Check if tenant has access to both module and channel
    if (!hasModuleAccess(moduleName) || !hasChannelAccess(channelName)) {
      return false;
    }

    // Check compatibility
    const compat = compatibility.find(
      c => c.module_name === moduleName && c.channel_name === channelName
    );

    return compat ? ['full', 'partial'].includes(compat.compatibility_level) : false;
  };

  // Configuration update function
  const updateTenantConfig = async (
    tenantId: string,
    config: Partial<TenantRuntimeConfig>
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('tenants')
        .update(config)
        .eq('id', tenantId);

      if (error) throw error;

      // Update local state
      if (selectedTenant?.id === tenantId) {
        setCurrentConfig(prev => prev ? { ...prev, ...config } : null);

        // Invalidate cache to force refresh on next load
        if (runtimeDataCache && runtimeDataCache.tenantId === tenantId) {
          runtimeDataCache = null;
          console.log('useRuntimeAccess: Cache invalidated after config update');
        }
      }

      return true;
    } catch (err) {
      console.error('Error updating tenant config:', err);
      setError(err instanceof Error ? err.message : t('runtime.updateFailed'));
      return false;
    }
  };

  return {
    modules,
    channels,
    compatibility,
    planTemplates,
    currentConfig,
    hasModuleAccess,
    hasChannelAccess,
    canUseModuleWithChannel,
    updateTenantConfig,
    isLoading,
    error,
  };
};